/**
 * Performance calculation utilities for V5000 metrics
 * Handles throughput, IOPS, and encoding scheme calculations
 */

import { parseEncodingScheme } from "../../utils/configUtilities";


/**
 * Get the appropriate JBOD key for metricsDb lookup
 * @param {number} jbodSize - JBOD size (78, 108, 60, 120) comes from config.jbodSize
 * @returns {string} Key for metricsDb (e.g., 'jbod78', 'jbod108')
 */
export const getJbodKey = (jbodSize) => {
    if (!isNaN(jbodSize)) {
        return `jbod${jbodSize}`;
    }
    return 'jbod78'; // Default fallback
};

/**
 * Determine compression state from config. 
 * 
 * MORK TODO : we will eventually calculate based on hybrid or afa or other system params.
 * 
 * @param {Object} config - Configuration object
 * @returns {string} 'compressed' or 'uncompressed'
 */
export const getCompressionState = (compressionRatio) => {
    // If compression ratio > 1, consider it compressed
    return compressionRatio === 1 ? 'uncompressed' : 'compressed';
};



/**
 * Calculate write efficiency factor from encoding scheme
 * @param {string} encodingScheme - Format "D+P" (e.g., "4+2")
 * @returns {number} Write efficiency factor (0-1)
 */
export const calculateWriteEfficiency = (encodingScheme) => {
    const { dataBits, parityBits } = parseEncodingScheme(encodingScheme);
    return dataBits / (dataBits + parityBits);
};

/**
 * Calculate total IOPS across nodes
 * @param {number} nodeCount - Number of nodes
 * @param {number} iopsPerNode - IOPS per node in millions
 * @returns {number} Total IOPS in millions
 */
export const calculateIops = (nodeCount, iopsPerNode) => {
    return nodeCount * iopsPerNode;
}

/**
 * Calculate total throughput across nodes
 * @param {number} nodeCount - Number of nodes
 * @param {number} throughputPerNode - Throughput per node in GB/s
 * @returns {number} Total throughput in GB/s
 */
export const calculateThroughput = (nodeCount, throughputPerNode) => {
    return nodeCount * throughputPerNode;
}


/**
 * CONF-93. Effective per-node flash throughput is the lower of what the card can
 * carry and what the transport can drive, read and write taken independently.
 *
 * An ABSENT ceiling means "no known cap", not zero — the caller must not read a
 * missing entry as 0, or an unmeasured card would silently zero out its quotes.
 * A missing protocol rate does mean zero: that is unmeasured throughput, and
 * showing it as 0 is the intended, conspicuous behaviour.
 *
 * The "100GbE always reports its default metrics" requirement needs no branch
 * on the way UP — a 40/40 ceiling cannot be LIFTED by either protocol, since
 * the result is the lower of the two. It can still be lowered, and the two
 * directions are resolved independently: on compressed RDMA (45/30) a 100GbE
 * node reports 40/30, its read clamped by the card and its write not. Neither
 * half is special-cased.
 *
 * @param {Object} metricsDb - vendor metrics database
 * @param {string} networkOption - option id, e.g. 'NDR/400GbE'
 * @param {string} protocol - 'TCP' | 'RDMA'
 * @param {string} compressionState - 'compressed' | 'uncompressed'
 * @param {string} quarter - e.g. '2026-Q3'
 * @param {string} nodeModel - 'standard' | 'dualCpu' (CONF-210)
 * @param {string} mediaType - 'tlc' | 'qlc' (CONF-221); from utils/ssdMediaType
 * @returns {{read: number, write: number}} GB/s per node
 */
export const resolveNetworkThroughput = (metricsDb, networkOption, protocol, compressionState, quarter, nodeModel = 'standard', mediaType = 'tlc') => {
    // CONF-210: a flash node model other than the single-CPU default reads its
    // own sibling table under network.nodeModels. A model with no table here
    // resolves to 0/0 — conspicuous, not silently single-CPU; the vendor gate
    // in dualCpuRules keeps that unreachable for vendors without the node.
    const net = nodeModel === 'standard'
        ? metricsDb?.network
        : metricsDb?.network?.nodeModels?.[nodeModel];
    const ceiling = net?.cardCeiling?.[networkOption];
    const rate = net?.protocolRate?.[protocol]?.[compressionState];

    const rateRead = rate?.readThroughputPerNode?.[quarter] || 0;
    const rateWrite = rate?.writeThroughputPerNode?.[quarter] || 0;

    // CONF-221: the media ceiling is a property of the drive, not the node, so
    // it is read from the top-level network table whichever node model is in
    // play. Same contract as the card ceiling: absent means "no cap". A rate
    // already below it (compressed TCP/RDMA) is left where it is — this is a
    // ceiling, not a value.
    const media = metricsDb?.network?.mediaCeiling?.[mediaType];

    const ceilRead = lowest(ceiling?.readThroughputPerNode?.[quarter], media?.readThroughputPerNode?.[quarter]);
    const ceilWrite = lowest(ceiling?.writeThroughputPerNode?.[quarter], media?.writeThroughputPerNode?.[quarter]);

    return {
        read: ceilRead == null ? rateRead : Math.min(ceilRead, rateRead),
        write: ceilWrite == null ? rateWrite : Math.min(ceilWrite, rateWrite),
    };
};

// min() over ceilings where an absent (null/undefined) ceiling means "no cap"
// rather than 0. Returns undefined when nothing caps.
const lowest = (...caps) => {
    const known = caps.filter((c) => c != null);
    return known.length ? Math.min(...known) : undefined;
};
